// @flow
import React, {useState} from 'react';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import {
  Box,
  IconButton,
  makeStyles,
  Popover,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {getImageUrl} from '../../../../utils/image_provider';
import withoutImage from '../../../../assets/img/withoutImage.png';
import {colors} from '../../../../assets/styles/colors';

import {
  DeleteOutline,
  Edit,
  Star,
  DragIndicator,
  Add,
  MoreVert,
} from '@material-ui/icons';

import type {Category} from '../../../../entities';
import {useIsFeatureInTier} from '../../../../utils/component';
import {SortableItem, SortableKnob} from 'react-easy-sort';
import {classNames, useToggle} from '../../../../utils/react';
import SubcategoryList from '../Subcategory/SubcategoryList';
import SubcategoryModal from '../SubcategoryModal/SubcategoryModal';
import {useActions} from '../../../../utils/globalStore';
import {actions} from '../../../sub-categories/duck';
import TiendadaTooltip from '../../../../elements/tiendada-tooltip';
import {TiendadaSwitch} from '../../../../elements/tiendada-switch';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';

type Props = {
  category: Category,
  handleDeleteItem: (categoryId: string) => void,
  handleEditItem: (category: Category) => void,
  onToggleFeatured: (categoryId: string) => void,
  onToggleEnabled: (categoryId: string, enabled: string) => void,
  featured: boolean,
};

const useStyles = makeStyles(theme => ({
  categorySortContainer: {
    width: '100%',
    position: 'relative',
    background: colors.grey200,
    borderRadius: '.5rem',
  },
  categoryItem: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '.5rem',
    background: 'inherit',
    paddingLeft: '0',
    borderRadius: '.5rem',
    position: 'relative',
    flexDirection: 'column',
  },
  '@global': {
    '.dragged': {
      transform: 'rotate(8deg)',
      boxShadow: `0px 8px 24px rgba(66, 64, 64, 0.12), 0 0 0 1px ${colors.primary}`,
      backgroundColor: colors.lightPurple,
      zIndex: 1000,
    },
  },
  itemContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexGrow: 1,
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
  },
  horizontalDivider: {
    width: 2,
    height: '35px',
    backgroundColor: '#D4D4DD',
    margin: 'auto 12px',
  },
  itemName: {
    color: colors.grey800,
    fontSize: '.75rem',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemThumb: {
    width: '40px',
    flexShrink: 0,
    height: 'auto',
    borderRadius: '8px',
    objectFit: 'cover',
    marginRight: '.5rem',
  },
  noImg: {
    padding: '.75rem',
    border: '1px dashed #B8B8C7',
  },
  trashIcon: {
    color: '#E54D7B',
    float: 'right',
    cursor: 'pointer',
  },
  editIcon: {
    color: '#717190',
    float: 'right',
    cursor: 'pointer',
  },
  iconButton: {
    padding: '0',

    [theme.breakpoints.up('sm')]: {
      padding: '.5rem',
    },
  },
  featuredIcon: {
    color: '#FFC107',
  },
  dragIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.grey600,
    alignSelf: 'normal',

    '&:hover': {
      cursor: 'grab',
      color: colors.primary,
    },
    '&:active': {
      cursor: 'grabbing',
    },
    [theme.breakpoints.up('sm')]: {
      padding: '.25rem',
    },

    [theme.breakpoints.up('md')]: {
      padding: '.5rem',
    },
  },
  badge: {
    color: colors.primary,
    background: colors.lightPurple,
  },
  popoverPaper: {
    padding: '.5rem',
    borderRadius: '.75rem',
    backdropFilter: 'blur(1rem)',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    boxShadow: '0 4px 1.5rem rgba(0, 0, 0, 0.1)',
  },
}));

function CategoryRowComponent(props: Props) {
  const {
    category,
    handleDeleteItem,
    handleEditItem,
    onToggleFeatured,
    onToggleEnabled,
    featured,
  } = props;
  const classes = useStyles();

  const theme = useTheme();
  const isLargeMobile = useMediaQuery(theme.breakpoints.up('sm'));

  const [popoverElement, setPopoverElement] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [openSubcategories, toggleSubcategories] = useToggle(false);
  const [modalSubcategoryOpen, toggleModalSubcategoryOpen] = useToggle(false);

  // const isFromExternalResource = !!category?.metadataSetting?.source;
  const isFromExternalResource = false;

  const userHasProductCategoriesFeature = useIsFeatureInTier(
    'PRODUCT_CATEGORIES'
  );

  const createSubcategory = useActions(actions.createSubcategory);

  const hasSubcategories =
    category.subCategories && category.subCategories.length > 0;

  const portraitImageId = category.portraitImageId;

  const imageSrc = portraitImageId
    ? getImageUrl(portraitImageId)
    : withoutImage;

  const handleDeleteClick = () => {
    setPopoverElement(null);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    handleDeleteItem(category.id);
    setConfirmDeleteOpen(false);
  };

  return (
    <div className={classes.categoryItem}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexGrow={1}
      >
        <SortableKnob>
          <span className={classes.dragIndicator}>
            <DragIndicator fontSize="small" />
          </span>
        </SortableKnob>
        <img
          className={classNames(classes.itemThumb, {
            [classes.noImg]: !portraitImageId,
          })}
          src={imageSrc}
          alt="Category"
        />
        <div className={classes.itemContent}>
          <span className={classes.itemName}>{category.name}</span>
          <div className={classes.rightSection}>
            {userHasProductCategoriesFeature && (
              <TiendadaTooltip title="Destacar categoría" placement="top">
                <IconButton
                  onClick={e => {
                    e.stopPropagation();
                    onToggleFeatured(category.id);
                  }}
                  className={classes.iconButton}
                  size="small"
                >
                  <Star
                    fontSize="small"
                    className={
                      featured ? classes.featuredIcon : classes.editIcon
                    }
                  />
                </IconButton>
              </TiendadaTooltip>
            )}

            <TiendadaTooltip
              title="Activar/Desactivar categoría"
              placement="top"
            >
              <div>
                <TiendadaSwitch
                  checked={category.enabled}
                  onChange={e => {
                    onToggleEnabled(category.id, e.target.checked);
                  }}
                  size="small"
                />
              </div>
            </TiendadaTooltip>

            {!isLargeMobile && (
              <>
                <TiendadaTooltip title="Opciones" placement="top">
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      setPopoverElement(e.currentTarget);
                    }}
                    className={classes.iconButton}
                    size="small"
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TiendadaTooltip>
                <Popover
                  open={Boolean(popoverElement)}
                  anchorEl={popoverElement}
                  onClose={() => setPopoverElement(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'end',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'end',
                  }}
                  PaperProps={{
                    className: classes.popoverPaper,
                  }}
                >
                  <Box
                    flexDirection="column"
                    sx={{gap: '.25rem'}}
                    display="flex"
                    alignItems="flex-start"
                  >
                    <CustomButton
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteClick();
                      }}
                      size="small"
                      className={classes.trashIcon}
                      startIcon={
                        <DeleteOutline fontSize="small" color="inherit" />
                      }
                    >
                      Eliminar
                    </CustomButton>

                    <CustomButton
                      onClick={e => {
                        e.stopPropagation();
                        handleEditItem(category);
                      }}
                      size="small"
                      startIcon={<Edit fontSize="small" />}
                    >
                      Editar
                    </CustomButton>
                  </Box>
                </Popover>
              </>
            )}

            {!isFromExternalResource && isLargeMobile && (
              <>
                <TiendadaTooltip title="Editar categoría" placement="top">
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      handleEditItem(category);
                    }}
                    size="small"
                    className={classes.iconButton}
                  >
                    <Edit fontSize="small" className={classes.editIcon} />
                  </IconButton>
                </TiendadaTooltip>
                <TiendadaTooltip title="Eliminar categoría" placement="top">
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteClick();
                    }}
                    size="small"
                    className={classes.iconButton}
                  >
                    <DeleteOutline
                      fontSize="small"
                      className={classes.trashIcon}
                    />
                  </IconButton>
                </TiendadaTooltip>
              </>
            )}

            {hasSubcategories ? (
              <TiendadaTooltip title="Mostrar subcategorías" placement="top">
                <IconButton
                  onClick={toggleSubcategories}
                  className={classes.iconButton}
                  size="medium"
                >
                  {openSubcategories ? (
                    <ArrowDropUpIcon />
                  ) : (
                    <ArrowDropDownIcon />
                  )}
                </IconButton>
              </TiendadaTooltip>
            ) : (
              <TiendadaTooltip title="Agregar subcategoría" placement="top">
                <IconButton
                  onClick={toggleModalSubcategoryOpen}
                  className={classes.iconButton}
                  size="medium"
                >
                  <Add fontSize="small" />
                </IconButton>
              </TiendadaTooltip>
            )}
          </div>
        </div>
      </Box>
      {openSubcategories && (
        <SubcategoryList
          category={category}
          subcategories={category.subCategories ?? []}
        />
      )}
      <SubcategoryModal
        open={modalSubcategoryOpen}
        handleClose={toggleModalSubcategoryOpen}
        onSubmit={toggleSubcategories}
        categories={[category]}
        createSubcategory={createSubcategory}
      />
      <ConfirmModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        title="¿Eliminar categoría?"
        message={`¿Estás seguro de que deseas eliminar la categoría "${category.name}"? Esta acción no se puede deshacer.`}
        confirmButtonLabel="Eliminar"
        cancelButtonLabel="Cancelar"
      />
    </div>
  );
}

const CategoryRow = React.memo(CategoryRowComponent, (prevProps, nextProps) => {
  const rawPrevProps = JSON.stringify(prevProps);
  const rawNextProps = JSON.stringify(nextProps);

  return rawPrevProps === rawNextProps;
});

export default function SortableCategoryRow(props: Props) {
  const classes = useStyles();

  return (
    <SortableItem>
      <div className={classes.categorySortContainer}>
        <CategoryRow {...props} />
      </div>
    </SortableItem>
  );
}
